// Shared table chrome for Settings entity lists (Physicians, Facilities). Extracted verbatim from
// screen/PhysicianManagement/PhysicianList.js in MVP-31132 so both lists share the same header,
// row hover and hand-rolled numbered pagination styling.
import { Table } from "antd";
import styled, { createGlobalStyle } from "styled-components";

import { GRAY_75, BLACK, PRIMARY_600, WHITE_GRAY, GRAY_100, WHITE } from "../../../constant/colors";
import { fontWeights } from "../../../constant/styles";

export const TooltipGlobalStyle = createGlobalStyle`
  .ant-tooltip .ant-tooltip-inner {
    border-radius: 8px !important;
  }

  .settings-sort-tooltip.ant-tooltip .ant-tooltip-inner {
    background-color: rgba(0, 0, 0, 0.75) !important;
    color: ${WHITE} !important;
    border-radius: 8px !important;
  }

  .settings-sort-tooltip.ant-tooltip .ant-tooltip-arrow-content::before {
    background: rgba(0, 0, 0, 0.75) !important;
  }

  .ant-popover {
    .ant-popover-inner-content {
      padding: 6px 8px;
    }
  }
`;

export const StyledTable = styled(Table)`
  .ant-table-body {
    overflow-y: overlay !important;
  }

  .ant-table-thead
    > tr
    > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
    background: none;
  }

  .ant-table-thead > tr > th {
    background: ${GRAY_100};
    color: ${BLACK};
    font-weight: ${fontWeights.bold};
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.5px;
    padding: 10px 12px;
    border: none !important;
  }

  .ant-table-thead > tr:hover > th {
    background: ${GRAY_100} !important;
  }

  .ant-table-tbody > tr > td {
    padding: 10px 12px;
    color: ${BLACK};
  }

  .ant-table-tbody > tr.ant-table-placeholder > td {
    border-bottom: none;
    padding: 0;
  }

  /* Empty-state row is not interactive: no pointer, no hover tint (antd + the row rule below) */
  .ant-table-tbody > tr.ant-table-placeholder,
  .ant-table-tbody > tr.ant-table-placeholder:hover,
  .ant-table-tbody > tr.ant-table-placeholder:hover > td,
  .ant-table-tbody > tr.ant-table-placeholder > td {
    background: transparent !important;
    cursor: default;
  }

  .ant-table-tbody > tr:not(.ant-table-placeholder) {
    cursor: pointer;

    &:hover {
      background-color: ${GRAY_75};
    }
  }

  /* Figma Pagination (4012:3890): 4px gaps, 24px round pills, 12px/600 numbers,
     selected pill Primary 600 with white text, 24px chevrons. */
  .ant-pagination {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 24px;
    gap: 4px;

    .ant-pagination-total-text {
      font-size: 14px;
      color: ${BLACK};
      margin-right: 4px;
    }

    .ant-pagination-item {
      border: none;
      background: transparent;
      min-width: 24px;
      height: 24px;
      line-height: 24px;
      border-radius: 100px;
      padding: 0 8px;

      a {
        color: ${BLACK};
        font-size: 12px;
        font-weight: 600;
        padding: 0;
      }

      &:hover {
        background: ${WHITE_GRAY};
      }

      &.ant-pagination-item-active {
        background: ${PRIMARY_600};

        a {
          color: white;
        }

        &:hover {
          background: ${PRIMARY_600};
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      min-width: 24px;
      height: 24px;
      margin: 0 2px;
      display: flex !important;
      align-items: center;
      justify-content: center;
    }

    .ant-pagination-prev button,
    .ant-pagination-next button {
      border: none;
      background: transparent;
      color: ${BLACK};
      width: 24px;
      height: 24px;
      min-width: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 100px;
      padding: 0;
    }

    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
      display: flex;
      align-items: center;
      justify-content: center;
      border: none !important;
      background: transparent !important;
    }

    .ant-pagination-prev .ant-pagination-item-link .anticon,
    .ant-pagination-next .ant-pagination-item-link .anticon {
      display: none;
    }

    .ant-pagination-prev .ant-pagination-item-link::after {
      content: "";
      display: block;
      width: 9px;
      height: 16px;
      background-image: url("data:image/svg+xml,%3Csvg width='9' height='16' viewBox='0 0 9 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.25 0.75L1.25 7.75L8.25 14.75' stroke='%23000000' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }

    .ant-pagination-next .ant-pagination-item-link::after {
      content: "";
      display: block;
      width: 9px;
      height: 16px;
      background-image: url("data:image/svg+xml,%3Csvg width='9' height='16' viewBox='0 0 9 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.750001 14.75L7.75 7.75L0.75 0.75' stroke='%23000000' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }

    .ant-pagination-prev:not(.ant-pagination-disabled):hover button,
    .ant-pagination-next:not(.ant-pagination-disabled):hover button {
      background-color: ${GRAY_100};
    }

    .ant-pagination-disabled.ant-pagination-prev .ant-pagination-item-link::after {
      background-image: url("data:image/svg+xml,%3Csvg width='9' height='16' viewBox='0 0 9 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.25 0.75L1.25 7.75L8.25 14.75' stroke='%2398A2B3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }

    .ant-pagination-disabled.ant-pagination-next .ant-pagination-item-link::after {
      background-image: url("data:image/svg+xml,%3Csvg width='9' height='16' viewBox='0 0 9 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.750001 14.75L7.75 7.75L0.75 0.75' stroke='%2398A2B3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }

    .ant-pagination-disabled button {
      cursor: not-allowed;
    }

    .ant-pagination-disabled:hover button {
      background-color: transparent !important;
    }

    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      min-width: 24px;
      height: 24px;
      line-height: 24px;

      .ant-pagination-item-link {
        border: none;
        background: transparent;
      }
    }
  }
`;
